// Shared primitives — restyled to the "Hearth & Home" aesthetic.
// Class names (nb-card, nb-btn, nb-input, nb-badge) preserved so existing
// page code keeps working unchanged.

export function PageHeader({ title, subtitle, action, color = "#E8B96B" }) {
  return (
    <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 hh-fade-up">
      <div>
        <span
          className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border text-[11px] font-semibold tracking-wide uppercase"
          style={{ background: "rgba(212,162,76,0.12)", borderColor: "rgba(42,31,23,0.10)", color: "#5C4A3A" }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: color }}
          />
          familytable
        </span>
        <h1
          className="text-5xl sm:text-6xl font-extrabold leading-[0.95] tracking-tight"
          style={{ fontFamily: "var(--hh-display)" }}
          data-testid="page-title"
        >
          {title}
        </h1>
        {subtitle && (
          <p className="text-base mt-3 max-w-2xl" style={{ color: "#5C4A3A" }}>
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function NBButton({
  children,
  color,
  onClick,
  type = "button",
  disabled,
  className = "",
  testId,
  ...props
}) {
  // If no color is provided, render the dark "ink" pill (primary).
  const isInk = !color;
  const style = color
    ? { backgroundColor: color, color: "#2A1F17", borderColor: "rgba(42,31,23,0.10)" }
    : undefined;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`nb-btn ${isInk ? "nb-btn-ink" : ""} text-sm ${className}`}
      style={style}
      {...props}
    >
      {children}
    </button>
  );
}

export function NBCard({ children, color, className = "", hover = true, ...props }) {
  // `color` is treated as a soft tint; default keeps the warm card surface.
  const style = color && color !== "white" ? { backgroundColor: color } : undefined;
  return (
    <div
      className={`nb-card ${hover ? "nb-card-hover" : ""} p-6 ${className}`}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
}

export function NBInput({ className = "", testId, ...props }) {
  return <input data-testid={testId} className={`nb-input w-full ${className}`} {...props} />;
}

export function NBTextarea({ className = "", testId, ...props }) {
  return (
    <textarea
      data-testid={testId}
      className={`nb-input w-full min-h-[96px] resize-y leading-relaxed ${className}`}
      {...props}
    />
  );
}

export function NBSelect({ className = "", children, testId, ...props }) {
  return (
    <select
      data-testid={testId}
      className={`nb-input w-full appearance-none ${className}`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='none' stroke='%232A1F17' stroke-width='1.5' stroke-linecap='round' d='M2.5 4.5l3.5 3.5 3.5-3.5'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 0.85rem center",
        paddingRight: "2.25rem",
      }}
      {...props}
    >
      {children}
    </select>
  );
}

export function EmptyState({ title, subtitle, action, icon }) {
  return (
    <div className="nb-card p-14 text-center hh-fade-up" data-testid="empty-state">
      <div
        className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5"
        style={{ background: "rgba(212,162,76,0.18)", color: "#2A1F17" }}
      >
        <span className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>
          {icon || "·"}
        </span>
      </div>
      <h3 className="text-2xl font-extrabold mb-2" style={{ fontFamily: "var(--hh-display)" }}>
        {title}
      </h3>
      {subtitle && (
        <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: "#5C4A3A" }}>
          {subtitle}
        </p>
      )}
      {action}
    </div>
  );
}

export function Avatar({ name, color = "#E8B96B", size = 40 }) {
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold"
      style={{
        backgroundColor: color,
        width: size,
        height: size,
        fontSize: size * 0.4,
        fontFamily: "var(--hh-display)",
        color: "#2A1F17",
        boxShadow: "inset 0 0 0 1px rgba(42,31,23,0.10), 0 6px 14px -8px rgba(42,31,23,0.25)",
      }}
    >
      {initials}
    </div>
  );
}
